import { useState, useEffect } from 'react'

import { useParams } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { FlaggedType, Space } from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { useSpace } from '@tribeplatform/react-sdk/hooks'
import { Container } from '@tribeplatform/react-ui-kit/Layout'

import { SpaceMembershipRequests } from '../Space/SpaceMembershipRequests.js'

import { DraftPosts } from './DraftAndSchedules/DraftPosts.js'
import { ScheduledPosts } from './DraftAndSchedules/ScheduledPosts.js'
import { ModerationSidebar } from './ModerationSidebar.js'
import { ModerationSummary } from './ModerationSummary.js'
import { PendingPosts } from './PendingPosts/index.js'
import { ReportedMembers } from './ReportedMembers/index.js'
import { ModerationTab } from './types.js'

export const Moderation = () => {
  const { section, spaceId } = useParams<{
    spaceId?: string
    section?: string
  }>()
  const { push } = useRouter()

  const [space, setSpace] = useState<Space | null>(null)
  const selectedTab: ModerationTab = (section as ModerationTab) ?? 'draft-posts'

  const { data } = useSpace({
    variables: { id: spaceId },
    useQueryOptions: { enabled: !!spaceId && spaceId !== 'all' },
  })

  useEffect(() => {
    if (data) {
      setSpace(data)
    }
  }, [data])

  const handleTabChange = (tab: ModerationTab) => {
    push(`/settings/moderation/${spaceId ?? 'all'}/${tab}`)
  }

  const handleSpaceChange = (maybeSpace: Space | null) => {
    if (!maybeSpace) {
      setSpace(null)
      push(`/settings/moderation/all/${selectedTab}`)
      return
    }

    const [canApproveSpaceMembershipRequest] = hasScopesPermission(maybeSpace, [
      'approveSpaceMembershipRequest',
    ])

    if (canApproveSpaceMembershipRequest) {
      setSpace(maybeSpace)
      push(`/settings/moderation/${maybeSpace?.id}/${selectedTab}`)
    }
  }

  return (
    <Container size="xl" direction="grid">
      <Container size="sm" padding={{ size: 'md', onlyOnMobile: true }}>
        <ModerationSidebar
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
          onSpaceChange={handleSpaceChange}
          maybeSpace={space}
        />
      </Container>
      <Container size="md" spacing="none">
        {selectedTab === 'pending-posts' && (
          <PendingPosts spaceId={space?.id} />
        )}
        {selectedTab === 'reported-posts' && (
          <PendingPosts spaceId={space?.id} flaggedBy={FlaggedType.MEMBER} />
        )}
        {selectedTab === 'member-requests' && (
          <SpaceMembershipRequests spaceId={space?.id} />
        )}
        {selectedTab === 'reported-members' && <ReportedMembers />}
        {selectedTab === 'draft-posts' && <DraftPosts spaceId={space?.id} />}
        {selectedTab === 'scheduled-posts' && (
          <ScheduledPosts spaceId={space?.id} />
        )}
      </Container>
      <Container size="sm" hide={{ default: false, sm: true, md: true }}>
        <ModerationSummary spaceId={space?.id} />
      </Container>
    </Container>
  )
}
